import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from 'context/AuthContext';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import type { ProductSetContextType } from '../types';

const ProductSetContext = createContext<ProductSetContextType | null>(null);

export const useProductSet = () => {
    const context = useContext(ProductSetContext);
    if (!context) throw new Error('useProductSet must be used within ProductSetProvider');
    return context;
};

export const ProductSetProvider = ({ children }: { children: ReactNode }) => {
    const { auth } = useAuth();
    const orderName = `voltox-${auth.user.ID}-product-sets-order`;
    const descName = `voltox-${auth.user.ID}-product-sets-desc`;

    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [setsOrder, setSetsOrder] = useState<string | false>(
        safeJSONParse(sessionStorage.getItem(orderName)) || '',
    );
    const [setsDesc, setSetsDesc] = useState(safeJSONParse(sessionStorage.getItem(descName)) === true);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(setsOrder));
    }, [setsOrder]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(setsDesc));
    }, [setsDesc]);

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    return (
        <ProductSetContext.Provider
            value={{
                showSpinner,
                setShowSpinner,
                setsOrder,
                setSetsOrder,
                setsDesc,
                setSetsDesc,
                listResetNonce,
                triggerListReset,
                listRefreshNonce,
                triggerListRefresh,
            }}
        >
            {children}
        </ProductSetContext.Provider>
    );
};
